import { useEffect, useMemo, useRef, useState } from 'react';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import { ExternalLink } from 'lucide-react';
import Prism from 'prismjs';

import type { Instruction, InstructionParam } from '../../data/instructions.generated';
import { useTransactionRunner } from '../../hooks/useTransactionRunner';
import { useCodeFlavor, type CodeFlavor } from '../../lib/code-flavor-context';
import { displayInfo } from '../../lib/instruction-display';
import { Console } from './Console';
import { MinimalCodeBlock, type CodeTab } from './CodeBlock';

interface InstructionPageProps {
  instruction: Instruction;
}

// Group → fetch account-type so the Console knows how to decode the new
// account that an instruction creates.
const GROUP_TO_ACCT: Record<string, string> = {
  config: 'config',
  borrower: 'borrower',
  contract: 'contract',
  rental: 'rental',
  affiliate: 'affiliate',
  reservation: 'reservation',
  discount: 'discount',
  member: 'member',
  vault: 'vault',
};

// TypeDoc carries `@example` content through verbatim, so multi-line code
// fences (```ts 400000```) leak into params.examples / defaultValue. Strip
// the fence + language tag down to the bare value. Comments are preserved
// here because the placeholder/example display benefits from them (e.g.
// "{ days: 30 } // 30 day maximum").
function cleanExample(raw: string | undefined): string {
  if (!raw) return '';
  const fence = raw.match(/```(?:[a-zA-Z]+)?\s*([\s\S]*?)```/);
  const inner = (fence ? fence[1] : raw).trim();
  return inner;
}

// Same fence strip, plus comment removal — used when seeding the input
// value so the field holds a parseable literal, not a hint string.
// Wrapping quotes / backticks are stripped too, since `quoteLiteral`
// re-quotes string values when building the snippet (otherwise we'd
// double-quote and emit `'\'@daily\''`).
function cleanForValue(raw: string | undefined): string {
  const stripped = cleanExample(raw)
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .split('\n')
    .map(line => line.replace(/\s*\/\/.*$/, ''))
    .join('\n')
    .trim();
  return unwrapQuotes(stripped);
}

function unwrapQuotes(value: string): string {
  if (value.length < 2) return value;
  const first = value[0];
  const last = value[value.length - 1];
  if ((first === "'" || first === '"' || first === '`') && first === last) {
    return value.slice(1, -1);
  }
  return value;
}

function quoteLiteral(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return "''";
  if (trimmed === 'true' || trimmed === 'false' || trimmed === 'null') return trimmed;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
  if (/^[{[]/.test(trimmed)) return trimmed;
  return `'${trimmed.replace(/'/g, "\\'")}'`;
}

function buildSnippet(
  instruction: Instruction,
  values: Record<string, string>,
  flavor: CodeFlavor
): string {
  const importPath = flavor === 'kit' ? '@sly-rentals/core' : '@sly-rentals/core/legacy';
  // Both entries return arrays of instructions (kit wraps them in
  // `InstructionResult`, legacy returns a plain `TransactionInstruction[]`),
  // so the variable should be plural in both flavors.
  const lines: string[] = [
    `import { ${instruction.name} } from '${importPath}';`,
    '',
    `const instructions = await ${instruction.name}({`,
  ];
  for (const p of instruction.params) {
    const v = values[p.name] ?? '';
    if (p.optional && !v.trim()) continue;
    lines.push(`  ${p.name}: ${quoteLiteral(v)},`);
  }
  lines.push('});');
  return lines.join('\n');
}

interface SchemaRowProps {
  param: InstructionParam;
  value: string;
  onChange: (next: string) => void;
  oneOfGroup?: string[];
}

// Pure-string fields (addresses, signers) don't benefit from syntax
// highlighting — they're long opaque base58 blobs that Prism would just
// turn into one solid colour. Reserve the highlighted overlay for fields
// that take real code-like input (numbers, object literals, schedules).
const HIGHLIGHTABLE_KINDS: ReadonlySet<string> = new Set([
  'amount',
  'duration',
  'schedule',
  'number',
  'boolean',
  'unknown',
]);

function highlightCode(source: string): string {
  // ` ` keeps the overlay block from collapsing when value is empty.
  return Prism.highlight(source || ' ', Prism.languages.typescript, 'typescript');
}

function SchemaRow({ param, value, onChange, oneOfGroup }: SchemaRowProps) {
  const example = cleanExample(param.examples[0]);
  const defaultValue = cleanExample(param.defaultValue);
  // Placeholder doubles as the example hint — it surfaces when the
  // field is empty and disappears as soon as the user starts typing,
  // so a separate "example: …" line above the input is redundant.
  const placeholder = example || (defaultValue ? `default: ${defaultValue}` : 'value');
  const highlight = HIGHLIGHTABLE_KINDS.has(param.typeKind) && value.length > 0;
  const html = useMemo(() => (highlight ? highlightCode(value) : ''), [highlight, value]);
  const tagKind = oneOfGroup ? 'required' : param.optional ? 'optional' : 'required';
  const tagLabel = oneOfGroup
    ? `one of: ${oneOfGroup.join(' / ')}`
    : param.optional
      ? 'optional'
      : 'required';
  return (
    <div className="schema-row">
      <div className="schema-meta">
        <span className="schema-name">{param.name}</span>
        <span className="schema-type">{param.typeKind}</span>
        <span className={`schema-tag ${tagKind}`}>{tagLabel}</span>
      </div>
      {param.description && <p className="schema-desc">{param.description}</p>}
      <div className={`schema-input-wrap${highlight ? ' is-highlighted' : ''}`}>
        {highlight && (
          <pre className="schema-input-overlay" aria-hidden>
            <code dangerouslySetInnerHTML={{ __html: html }} />
          </pre>
        )}
        <input
          className="schema-input"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          spellCheck={false}
        />
      </div>
    </div>
  );
}

// Every optional param goes under the "Advanced parameters" toggle.
// Some have a TSDoc @default the SDK applies at runtime; others are
// truly opt-in (cancelDelayMin override, discount auth, third-party
// payer, etc). Either way, the common case is "skip them" — the
// snippet builder already omits empty optionals so the emitted code
// stays clean. Params named in a `requiresOneOf` group escape Advanced
// because at least one of them must be provided.
function isAdvancedParam(p: InstructionParam, oneOfNames: ReadonlySet<string>): boolean {
  if (oneOfNames.has(p.name)) return false;
  return p.optional;
}

export function InstructionPage({ instruction }: InstructionPageProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const { flavor, setFlavor } = useCodeFlavor();
  const runner = useTransactionRunner();
  const { primaryWallet } = useDynamicContext();
  const walletAddr = primaryWallet?.address ?? '';
  const prevWalletAddr = useRef('');
  // Derive the fetch account-type from the sidebar grouping itself —
  // the subgroup (Thread / Fleet) is the right answer when set, falling
  // back to the parent group. This keeps the auto-fetch lined up with
  // what the user actually clicked on, without a per-instruction map.
  const acct =
    displayInfo(instruction.name, instruction.group).subgroup ??
    GROUP_TO_ACCT[instruction.group] ??
    'contract';

  // Stable sort that puts signers first within each bucket — signers
  // are the load-bearing input ("who's authorizing this?"), so they
  // should never sit below an address or amount. The rest of the
  // ordering follows the SDK source.
  const orderParams = (params: InstructionParam[]): InstructionParam[] => {
    const indexed = params.map((p, i) => ({ p, i }));
    indexed.sort((a, b) => {
      const aIsSigner = a.p.typeKind === 'signer' ? 0 : 1;
      const bIsSigner = b.p.typeKind === 'signer' ? 0 : 1;
      if (aIsSigner !== bIsSigner) return aIsSigner - bIsSigner;
      return a.i - b.i;
    });
    return indexed.map(x => x.p);
  };

  const oneOfNames = useMemo<ReadonlySet<string>>(
    () => new Set((instruction.requiresOneOf ?? []).flat()),
    [instruction.requiresOneOf]
  );
  const oneOfGroupFor = (name: string): string[] | undefined =>
    (instruction.requiresOneOf ?? []).find(group => group.includes(name));

  const regularParams = useMemo(
    () => orderParams(instruction.params.filter(p => !isAdvancedParam(p, oneOfNames))),
    [instruction.params, oneOfNames]
  );
  const advancedParams = useMemo(
    () => orderParams(instruction.params.filter(p => isAdvancedParam(p, oneOfNames))),
    [instruction.params, oneOfNames]
  );

  // Reset form + status when navigating between instructions, then seed
  // required + non-defaulted optional fields from the first @example tag.
  // Advanced (optional + @default) fields are intentionally left empty so
  // the snippet stays clean — the SDK applies the default when the param
  // is undefined. Signer fields get the connected wallet via the effect
  // below.
  useEffect(() => {
    const seeded: Record<string, string> = {};
    for (const p of instruction.params) {
      if (p.typeKind === 'signer' && walletAddr) {
        seeded[p.name] = walletAddr;
        continue;
      }
      if (isAdvancedParam(p, oneOfNames)) continue;
      const fill = cleanForValue(p.examples[0]);
      if (fill) seeded[p.name] = fill;
    }
    setValues(seeded);
    setShowAdvanced(false);
    runner.reset();
    prevWalletAddr.current = walletAddr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instruction.name]);

  // Re-fill every signer field when the user signs in or switches
  // wallets. Manual edits are preserved — a signer field is only refilled
  // if it's empty or still equal to the previously-seeded wallet address.
  useEffect(() => {
    if (!walletAddr) return;
    const signerNames = instruction.params.filter(p => p.typeKind === 'signer').map(p => p.name);
    if (signerNames.length === 0) return;
    setValues(prev => {
      let changed = false;
      const next = { ...prev };
      for (const name of signerNames) {
        const current = next[name] ?? '';
        if (current && current !== prevWalletAddr.current) continue;
        if (current === walletAddr) continue;
        next[name] = walletAddr;
        changed = true;
      }
      return changed ? next : prev;
    });
    prevWalletAddr.current = walletAddr;
  }, [walletAddr, instruction.name, instruction.params]);

  const update = (name: string, next: string) => setValues(prev => ({ ...prev, [name]: next }));

  const tabs = useMemo<CodeTab[]>(
    () => [
      { id: 'kit', label: '@solana/kit', source: buildSnippet(instruction, values, 'kit') },
      {
        id: 'web3js',
        label: '@solana/web3.js',
        source: buildSnippet(instruction, values, 'web3js'),
      },
    ],
    [instruction, values]
  );

  return (
    <div className="ix-grid">
      <div>
        <div className="ix-head">
          {(() => {
            const info = displayInfo(instruction.name, instruction.group);
            const subSegments = info.subgroup ? info.subgroup.split('/') : [];
            return (
              <div className="ix-kicker">
                <span className="br">[</span>
                <span className="group">&nbsp;{instruction.group}</span>
                {subSegments.map(seg => (
                  <span key={seg}>
                    <span className="br">/</span>
                    <span className="group">&nbsp;{seg}</span>
                  </span>
                ))}
                <span className="br">/</span>
                <span className="name">&nbsp;{info.display}&nbsp;</span>
                <span className="br">]</span>
              </div>
            );
          })()}
          <h1 className="ix-title">
            {instruction.name}
            <a
              href={`https://wuwei-labs.github.io/srsly/api/srsly/functions/index.${instruction.name}.html`}
              target="_blank"
              rel="noopener"
              className="ix-docs-link"
              title={`Open ${instruction.name} reference docs`}
            >
              docs <ExternalLink size={11} strokeWidth={1.6} />
            </a>
            {instruction.isAdmin && (
              <span className="ix-badges">
                <span className="ix-badge admin">admin</span>
              </span>
            )}
          </h1>
          {instruction.description && <p className="ix-sub">{instruction.description}</p>}
        </div>

        <h2 className="ix-section-label">Parameters</h2>
        {instruction.params.length === 0 ? (
          <p className="dim mono" style={{ padding: '14px 0', fontSize: 12 }}>
            No parameters.
          </p>
        ) : (
          <>
            {regularParams.length === 0 && (
              <p className="dim mono" style={{ padding: '14px 0', fontSize: 12 }}>
                No required parameters — all optional. Expand below to override.
              </p>
            )}
            {regularParams.length > 0 && (
              <div className="schema">
                {regularParams.map(p => (
                  <SchemaRow
                    key={p.name}
                    param={p}
                    value={values[p.name] ?? ''}
                    onChange={v => update(p.name, v)}
                    oneOfGroup={oneOfGroupFor(p.name)}
                  />
                ))}
              </div>
            )}
            {advancedParams.length > 0 && (
              <div className="schema-advanced">
                <button
                  type="button"
                  className="schema-advanced-toggle"
                  onClick={() => setShowAdvanced(v => !v)}
                  aria-expanded={showAdvanced}
                >
                  <span className="caret">{showAdvanced ? '▾' : '▸'}</span>
                  Advanced parameters
                  <span className="count">({advancedParams.length})</span>
                </button>
                {showAdvanced && (
                  <div className="schema">
                    {advancedParams.map(p => (
                      <SchemaRow
                        key={p.name}
                        param={p}
                        value={values[p.name] ?? ''}
                        onChange={v => update(p.name, v)}
                        oneOfGroup={oneOfGroupFor(p.name)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <aside
        style={{
          position: 'sticky',
          top: 24,
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2 className="ix-section-label">Live preview</h2>
        <MinimalCodeBlock
          tabs={tabs}
          active={flavor}
          onActiveChange={id => setFlavor(id as CodeFlavor)}
        />
        <Console
          mode="instruction"
          accountType={acct}
          status={runner.status}
          onSubmit={runner.canRun ? () => runner.run(instruction, values) : undefined}
          onReset={runner.reset}
          onFetch={runner.fetchAccount}
        />
        {!runner.canRun && (
          <span className="dim mono" style={{ fontSize: 10.5, lineHeight: 1.5 }}>
            {runner.reason}
          </span>
        )}
      </aside>
    </div>
  );
}
